import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { NoDataMessage, SpinLoader } from "@/components/atoms";
import { contractApi } from "@/shared/apis";
import { ContractCategory, defaultContractPagination } from "@/shared/domain";
import { useGlobalFilter, useNavigation } from "@/shared/hooks";
import { StyledCategoriesPanel, StyledItemsPanel } from "@/styles/styled";
import * as tagManager from "@/tagManager";

import ContractCategoryItem from "../components/ContractCategoryItem/ContractCategoryItem";
import ContractItem from "../components/ContractItem/ContractItem";
import useContractQueries from "../useContractQueries";

const MAX_CONTRACTS_ITEMS = 3;
const defaultPage = { page: 1, pageSize: 5 };
const ALL_CONTRACTS_ID = "0";

const ContractPanel: FC = () => {
    const { t } = useTranslation();
    const { navToContracts } = useNavigation();
    const { policyholdersGF } = useGlobalFilter();

    const { useContractsCategoriesQuery } = useContractQueries();

    const {
        data: contractsCount = 0,
        isLoading: isLoadingContractsCount,
        isFetched: isFetchedContractsCount,
    } = useQuery(
        ["contractsCount", { ...defaultPage, ALL_CONTRACTS_ID, policyholdersGF }],
        () => contractApi.getContractsCount(policyholdersGF, ALL_CONTRACTS_ID),
        {
            enabled: !!policyholdersGF.length,
            // suspense: false,
        },
    );

    const isContractItemsView =
        !isLoadingContractsCount && contractsCount !== 0 && contractsCount <= MAX_CONTRACTS_ITEMS;
    const isCategoryView = !isLoadingContractsCount && contractsCount > MAX_CONTRACTS_ITEMS;

    const {
        data: contractsPaginated = defaultContractPagination,
        isLoading: isLoadingContracts,
        error: contractError,
    } = useQuery(
        ["contracts", { ...defaultPage, ALL_CONTRACTS_ID, policyholdersGF }],
        () =>
            contractApi.getContracts({
                policyholdersIdList: policyholdersGF,
                categoryCode: +ALL_CONTRACTS_ID,
                ...defaultPage,
            }),
        {
            enabled: isContractItemsView,
            // suspense: false,
        },
    );

    const { result: contracts } = contractsPaginated;

    const { contractCategories, isLoadingCategories, error } = useContractsCategoriesQuery({
        enabled: isCategoryView,
    });

    if (error) {
        throw error;
    }
    if (contractError) {
        throw contractError;
    }
    const handleCategoryClick = (contractCategory: ContractCategory) => {
        return navToContracts(contractCategory.category.id);
    };

    // we can't use suspense here otherwise the two APi call are not in parallel (PeterC)
    if (isLoadingContractsCount || isLoadingCategories || isLoadingContracts || !isFetchedContractsCount) {
        return <SpinLoader />;
    }

    // not contracts at all
    if (contractsCount === 0) {
        return <NoDataMessage>{t("Contracts.Empty")}</NoDataMessage>;
    }

    // less the 4 contract, show contract themselves
    if (!isCategoryView) {
        tagManager.pushViewItemListEvent("contract", "Contracts", true);
        return (
            <StyledItemsPanel>
                {contracts.map((contract) => (
                    <ContractItem key={`contractitem-${contract.id}`} contract={contract} />
                ))}
            </StyledItemsPanel>
        );
    }

    // More the 3 contracts, show categories
    const defaultCategory: ContractCategory = {
        totalItems: contractsCount,
        category: {
            id: ALL_CONTRACTS_ID,
            name: t("Common.All"),
        },
    };

    return (
        <>
            <StyledCategoriesPanel $isLoading={isLoadingCategories} data-testid="contract_panel-panel">
                {contractCategories.length > 1 && (
                    <ContractCategoryItem
                        onCategoryClick={() => handleCategoryClick(defaultCategory)}
                        contractCategory={defaultCategory}
                    />
                )}
                {contractCategories.map((contractCategory) => (
                    <ContractCategoryItem
                        key={`contractcategoryitem-${contractCategory.category.id}`}
                        onCategoryClick={() => handleCategoryClick(contractCategory)}
                        contractCategory={contractCategory}
                    />
                ))}
            </StyledCategoriesPanel>
        </>
    );
};

export default ContractPanel;
